import {
  DEFAULT_HOME_TITLE,
  DEFAULT_OG_IMAGE_PATH,
  SITE_DESCRIPTION_LONG,
  SITE_NAME,
  SITE_SAME_AS,
  SITE_TAGLINE,
  absoluteUrl,
  getSiteOrigin,
} from "../../config/site";

export type OgType = "website" | "article";

export type SeoPayload = {
  title: string;
  description: string;
  noindex?: boolean;
  /** Path under public/ or absolute URL for og:image */
  image?: string;
  type?: OgType;
  keywords?: string;
  /** Extra JSON-LD objects (merged into script tags on the page) */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function normalizePath(pathname: string): string {
  const noQuery = pathname.split("?")[0] || "/";
  if (noQuery === "/") return "/";
  return noQuery.replace(/\/$/, "") || "/";
}

/**
 * Single @graph for homepage: avoids duplicate @context blocks and invalid nested
 * NewsArticle on Organization (common cause of “critical” structured-data errors).
 */
function homePageJsonLd(): Record<string, unknown> {
  const origin = getSiteOrigin().replace(/\/$/, "");
  const orgId = `${origin}/#organization`;
  const websiteId = `${origin}/#website`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": orgId,
        name: SITE_NAME,
        url: `${origin}/`,
        logo: {
          "@type": "ImageObject",
          url: absoluteUrl(DEFAULT_OG_IMAGE_PATH),
        },
        description: SITE_DESCRIPTION_LONG,
        sameAs: [...SITE_SAME_AS],
      },
      {
        "@type": "WebSite",
        "@id": websiteId,
        name: SITE_NAME,
        url: `${origin}/`,
        inLanguage: "en-NG",
        description: SITE_DESCRIPTION_LONG,
        publisher: { "@id": orgId },
      },
    ],
  };
}

const ROUTES: { test: (path: string) => boolean; seo: SeoPayload }[] = [
  {
    test: (p) => p.startsWith("/admin"),
    seo: {
      title: "Admin",
      description: "Hoba Express administration and operations dashboard.",
      noindex: true,
    },
  },
  {
    test: (p) => p === "/partner" || p.startsWith("/partner/"),
    seo: {
      title: "Partner dashboard",
      description: "Logistics partner workspace for Hoba Express branches, staff, and orders.",
      noindex: true,
    },
  },
  {
    test: (p) => p === "/sign-in",
    seo: {
      title: "Sign in",
      description: "Sign in to your Hoba Express or partner account.",
      noindex: true,
    },
  },
  {
    test: (p) => p === "/dashb",
    seo: {
      title: "Dashboard",
      description: "Hoba Express dashboard.",
      noindex: true,
    },
  },
  {
    test: (p) => p === "/",
    seo: {
      title: DEFAULT_HOME_TITLE,
      description: SITE_TAGLINE,
      keywords:
        "Hoba Express, vehicle leasing, lease to own, motorcycle financing, mobility, Nigeria, asset management, riders",
      jsonLd: homePageJsonLd(),
    },
  },
  {
    test: (p) => p === "/riders",
    seo: {
      title: "Riders — Lease-to-own bikes & earnings with Hoba Express",
      description:
        "Become a Hoba Express rider: own your bike, earn flexibly, and repay in clear installments with our lease-to-own program in Nigeria.",
      keywords:
        "Hoba Express riders, delivery rider, lease to own bike, bike financing for riders",
    },
  },
  {
    test: (p) => p === "/about",
    seo: {
      title: "Riders — About Hoba Express programs & bike ownership",
      description:
        "Rider-focused overview: how Hoba Express helps you lease-to-own a bike, our mission, goals, and steps to get on the road in Nigeria.",
      keywords:
        "Hoba Express riders, rider about, lease to own bike, bike program Nigeria",
    },
  },
  {
    test: (p) => p === "/about-us",
    seo: {
      title: "About Hoba Express — Company, leasing & mobility in Nigeria",
      description:
        "Learn about Hoba Express across the full business: lease-to-own vehicles, interstate delivery, HOBA Mobility, HobaX, and how we support riders and partners.",
      keywords:
        "Hoba Express company, about Hoba, mobility Nigeria, lease to own, logistics",
    },
  },
  {
    test: (p) => p === "/all-vehicles" || p.startsWith("/all-vehicles/"),
    seo: {
      title: "Browse bikes — Lease-to-own bikes & fleet | Hoba Express",
      description:
        "Browse Hoba Express bikes and financing. Find bikes and fleet options for delivery, logistics, and personal mobility goals.",
      keywords: "Hoba bikes, lease bikes, delivery bikes, bike catalog",
    },
  },
  {
    test: (p) => p === "/faqs",
    seo: {
      title: "Hoba Express FAQs — Applications, payments & ownership",
      description:
        "Answers on Hoba Express applications, payments, bike ownership, and partner programs. Get clear guidance before you apply.",
      keywords: "Hoba Express FAQ, leasing questions, rider support",
    },
  },
  {
    test: (p) => p === "/blog",
    seo: {
      title: "Hoba Express news — Mobility, riders & lease-to-own updates",
      description:
        "News and stories from Hoba Express: mobility trends, rider success, lease-to-own programs, and company updates.",
      type: "article",
      keywords: "Hoba Express news, mobility blog, lease to own updates",
    },
  },
  {
    test: (p) => p === "/help-center",
    seo: {
      title: "Help center & contact — Hoba Express support",
      description:
        "Contact Hoba Express for help with applications, accounts, and services. Reach our team and find support resources quickly.",
      keywords: "Hoba Express contact, help center, support",
    },
  },
  {
    test: (p) => p === "/hoba-mobility",
    seo: {
      title: "Hoba Mobility — Ride-hailing & driver technology platform",
      description:
        "Hoba Mobility connects drivers and riders with technology for efficient, reliable on-demand trips and urban transportation.",
      keywords: "Hoba Mobility, ride hailing, mobility platform",
    },
  },
  {
    test: (p) => p.startsWith("/vehicle-details/"),
    seo: {
      title: "Bike details — Hoba Express listing",
      description:
        "View specifications and financing for this Hoba Express bike. Compare options before you apply or reserve.",
      keywords: "Hoba bike details, lease bike information",
    },
  },
  {
    test: (p) => p === "/partners",
    seo: {
      title: "Partner with Hoba Express — Logistics & fleet programs",
      description:
        "Grow your logistics network with Hoba Express: partner programs, rider support, and scalable operations across regions.",
      keywords: "Hoba Express partners, logistics partnership, fleet partnership",
    },
  },
  {
    test: (p) => p === "/company",
    seo: {
      title: "Hoba company — Express, Mobility & Hoba X ecosystem",
      description:
        "Explore Hoba: Express leasing, Mobility tech, and Hoba X in one ecosystem built for modern urban mobility and delivery.",
      keywords: "Hoba company, Hoba Express, Hoba Mobility, Hoba X",
    },
  },
  {
    test: (p) => p === "/interstate-delivery",
    seo: {
      title: "Interstate delivery Nigeria — Hoba Express parcel lanes",
      description:
        "Ship from Lagos to Onitsha, PH, Abuja, Owerri & Aba with Hoba Express. Promotional rates on small parcels; Ogudu drop-off. WhatsApp 09099843428.",
      keywords:
        "Hoba Express interstate, Nigeria parcel delivery, Lagos to Abuja courier, Onitsha shipping, Port Harcourt logistics",
      image: "/images/interstate-02.jpeg",
    },
  },
  {
    test: (p) => p === "/partners/onboard-partner",
    seo: {
      title: "Onboard as a Hoba Express logistics or mobility partner",
      description:
        "Start partner onboarding with Hoba Express: submit your details and join our logistics and mobility network in a few steps.",
      keywords: "partner onboarding Hoba, become a Hoba partner",
    },
  },
  {
    test: (p) => p === "/privacy-policy",
    seo: {
      title: "Privacy policy — How Hoba Express uses your data",
      description:
        "Read how Hoba Express collects, uses, and protects personal information when you use our websites, apps, and services.",
      keywords: "Hoba privacy policy, data protection",
    },
  },
  {
    test: (p) => p === "/track-application",
    seo: {
      title: "Track your Hoba Express rider or bike application",
      description:
        "Check your Hoba Express application status online. Follow a few quick steps for riders, bikes, or partner requests.",
      keywords: "track Hoba application, application status",
    },
  },
  {
    test: (p) => p === "/rider-application-form",
    seo: {
      title: "Rider application — Apply to Hoba Express online",
      description:
        "Apply as a Hoba Express rider: complete your profile, documents, and bike preferences securely in one streamlined flow.",
      keywords: "Hoba rider application, apply to be a rider, lease to own application",
    },
  },
];

const FALLBACK: SeoPayload = {
  title: SITE_NAME,
  description: SITE_TAGLINE,
  noindex: true,
};

export function getSeoForPath(pathname: string): SeoPayload {
  const path = normalizePath(pathname);
  for (const route of ROUTES) {
    if (route.test(path)) {
      return { ...route.seo };
    }
  }
  return { ...FALLBACK };
}

export function formatDocumentTitle(title: string): string {
  if (title === SITE_NAME || title.endsWith(`| ${SITE_NAME}`)) return title;
  if (title.includes(SITE_NAME) && title.length < 80) return title;
  return `${title} | ${SITE_NAME}`;
}

export function resolveOgImage(seo: SeoPayload): string {
  if (seo.image) {
    if (seo.image.startsWith("http")) return seo.image;
    return absoluteUrl(seo.image.startsWith("/") ? seo.image : `/${seo.image}`);
  }
  return absoluteUrl(DEFAULT_OG_IMAGE_PATH);
}
